// =============================================================================
// Copy for the local-SEO pages (cities and services), in Mexican Spanish.
//
// Every claim here is something the site already states (src/i18n/locales/
// es.json, src/data/content.js and the coverage map): based in Delicias, 33+
// years, own fleet in continuous renewal, own workshop, double GPS in every
// tractor and independent tracking in every trailer, 53-ft dry vans, one 53-ft
// Thermo refrigerated unit, storage as an extra, trip reports, optional cargo
// insurance, the coverage states/cities and the Altamira and Manzanillo ports.
// No prices, schedules, transit times, capacities or clients are stated.
// =============================================================================

const ESTADOS =
  "Chihuahua, Durango, Zacatecas, Aguascalientes, San Luis Potosí, Nuevo León, Tamaulipas, Jalisco, Guanajuato, Querétaro y Colima";

export const SEO_PAGES = {
  delicias: {
    path: "/transportes-en-delicias/",
    title: "Transportes en Delicias, Chihuahua | Transportes CGMG",
    description:
      "Transportes de carga en Delicias, Chih.: carga seca y refrigerada, flota propia y rastreo GPS 24/7. Cobertura nacional desde Delicias. Cotiza hoy.",
    breadcrumb: "Transportes en Delicias",
    hero: {
      eyebrow: "Delicias, Chihuahua",
      h1: "Transportes de carga en Delicias, Chihuahua",
      lead: "Somos una empresa de Delicias. Aquí están nuestra oficina, nuestro taller y el patio de donde salen las unidades que mueven la carga de nuestros clientes hacia el centro y norte del país.",
    },
    sections: [
      {
        title: "Una transportista local, con flota propia",
        paragraphs: [
          "Transportes CGMG nació en Delicias. Empezamos en 1993 como afiliados de Transportes Cimarrón y en 1999 nos constituimos con el nombre de nuestro fundador, el Ing. Carlos Gerardo Martínez Gómez. Más de 33 años después seguimos operando desde la misma ciudad.",
          "Cuando contrata un flete con nosotros, trata directamente con la empresa dueña de las unidades. Las unidades se revisan en nuestro propio taller, aquí en Delicias, y la flota se renueva de forma continua.",
        ],
      },
      {
        title: "Qué podemos mover por usted desde Delicias",
        list: [
          "Carga seca en cajas de 53 pies: mercancía general, paletizada o a granel.",
          "Carga refrigerada en unidad Thermo de 53 pies, con control de temperatura para producto perecedero.",
          "Almacenaje como servicio extra a su transporte.",
          "Rutas a la medida: estamos en la mejor disposición de cotizar nuevos orígenes y destinos.",
        ],
      },
      {
        title: "Desde Delicias hacia el resto del país",
        paragraphs: [
          `Delicias es el punto de partida de nuestra red. Desde aquí cubrimos rutas hacia ${ESTADOS}, con conexión a los puertos industriales de Altamira y Manzanillo.`,
          "Entre las ciudades de nuestra red están Chihuahua, Torreón, Saltillo, Monterrey, Durango, Zacatecas, Aguascalientes, San Luis Potosí, Guadalajara, Irapuato y Querétaro. Si su destino no aparece, pregúntenos: siempre estamos dispuestos a cotizar nuevos requerimientos.",
        ],
      },
      {
        title: "Su carga, visible en todo momento",
        paragraphs: [
          "Cada tracto-camión lleva doble sistema de GPS y cada remolque tiene rastreo independiente, con monitoreo las 24 horas, los 365 días del año. Podemos integrar el rastreo a la plataforma de su empresa y le entregamos un reporte documentado de cada viaje.",
        ],
      },
    ],
    faqs: [
      {
        q: "¿Cuánto cuesta un flete desde Delicias?",
        a: "Depende del destino, del tipo y peso de la carga, de si requiere refrigeración y de la frecuencia de los viajes. Envíenos esos datos por el formulario o llámenos y le preparamos una cotización para su caso.",
      },
      {
        q: "¿Dónde están sus oficinas en Delicias?",
        a: "Nuestras oficinas están en Calle 23 Nte. 404, Industrial Nte., 33000 Delicias, Chihuahua. En esta página encuentra el mapa para llegar.",
      },
      {
        q: "¿Tienen rastreo GPS?",
        a: "Sí. Todos nuestros tractos y remolques tienen GPS: doble sistema en cada tracto-camión y rastreo independiente en cada remolque, con monitoreo 24/7. También podemos integrarlo a la plataforma de su empresa.",
      },
      {
        q: "¿Manejan carga refrigerada?",
        a: "Sí. Contamos con unidad Thermo de 53 pies con control de temperatura para producto perecedero. Por ser equipo especializado, le recomendamos cotizar con anticipación para confirmar disponibilidad.",
      },
      {
        q: "¿Qué cobertura tienen desde Delicias?",
        a: `Cubrimos rutas del centro y norte de México (${ESTADOS}) y los puertos de Altamira y Manzanillo. Para otros destinos, cotizamos con gusto.`,
      },
    ],
    related: ["chihuahua", "seca", "refrigerada"],
  },

  chihuahua: {
    path: "/transportes-en-chihuahua/",
    title: "Transportes en Chihuahua | Autotransporte de carga CGMG",
    description:
      "Autotransporte de carga en Chihuahua con flota propia, rastreo GPS 24/7 y cobertura nacional. Base en Delicias, servicio en todo el estado. Cotiza tu flete.",
    breadcrumb: "Transportes en Chihuahua",
    hero: {
      eyebrow: "Estado de Chihuahua",
      h1: "Autotransporte de carga en Chihuahua",
      lead: "Una empresa chihuahuense que conecta al estado con el centro y norte de México: flota propia, rastreo GPS las 24 horas y más de 33 años en el autotransporte de carga.",
    },
    sections: [
      {
        title: "Carga desde y hacia el estado de Chihuahua",
        paragraphs: [
          "Si su empresa produce, almacena o distribuye en Chihuahua, necesita un transportista que conozca las rutas del estado y que responda cuando algo cambia. Operamos desde Delicias y la ciudad de Chihuahua forma parte de nuestra red de rutas.",
          "Movemos carga seca y carga refrigerada para empresas que necesitan cumplir fechas de entrega sin perder de vista su mercancía.",
        ],
      },
      {
        title: "Conexión con el centro y norte del país",
        paragraphs: [
          "Desde Chihuahua llegamos a Torreón, Saltillo, Monterrey, Durango, Zacatecas, Aguascalientes, San Luis Potosí, Guadalajara, Irapuato y Querétaro, y tenemos conexión directa con los puertos industriales de Altamira y Manzanillo.",
          "Próximamente incorporaremos el puerto de Ensenada a nuestra cobertura.",
        ],
      },
      {
        title: "Lo que obtiene al trabajar con CGMG",
        list: [
          "Flota propia en renovación continua y taller propio para el mantenimiento.",
          "Doble GPS en cada tracto-camión y rastreo independiente en cada remolque.",
          "Reportes de viaje documentados, entregados directamente al cliente.",
          "Seguro de carga disponible a solicitud.",
          "Personal en capacitación continua y precios competitivos, pensados para relaciones de largo plazo.",
        ],
      },
    ],
    faqs: [
      {
        q: "¿Cuánto cuesta un flete en Chihuahua?",
        a: "Cada flete se cotiza según origen, destino, tipo de carga, peso y frecuencia. Cuéntenos qué necesita mover y le respondemos con una cotización.",
      },
      {
        q: "¿Dan servicio en la ciudad de Chihuahua?",
        a: "Sí. Nuestra base está en Delicias y la ciudad de Chihuahua forma parte de nuestra red de rutas, conectada con el centro y norte del país.",
      },
      {
        q: "¿Tienen rastreo GPS?",
        a: "Sí, en el 100% de nuestros tractos y remolques, con monitoreo las 24 horas, los 365 días del año.",
      },
      {
        q: "¿Manejan carga refrigerada?",
        a: "Sí, con unidad Thermo de 53 pies con control de temperatura. Indíquenos el producto y el rango de temperatura que requiere para confirmar el servicio.",
      },
      {
        q: "¿Llevan carga a otros estados y a puertos?",
        a: `Sí. Cubrimos ${ESTADOS}, y los puertos de Altamira y Manzanillo.`,
      },
      {
        q: "¿Ofrecen seguro de carga?",
        a: "Sí, el seguro de carga está disponible a solicitud. Menciónelo al pedir su cotización.",
      },
    ],
    related: ["delicias", "seca", "refrigerada"],
  },

  seca: {
    path: "/carga-seca/",
    title: "Transporte de carga seca en Chihuahua | Transportes CGMG",
    description:
      "Transporte de carga seca en cajas de 53 pies desde Delicias, Chihuahua: mercancía general, paletizada o a granel, con rastreo GPS 24/7. Cotice su flete.",
    breadcrumb: "Carga seca",
    service: "Transporte de carga seca",
    hero: {
      eyebrow: "Servicios · Carga seca",
      h1: "Transporte de carga seca",
      lead: "Cajas secas de 53 pies para mercancía general, paletizada o a granel, con rastreo GPS en todo el trayecto y un equipo que le responde.",
    },
    sections: [
      {
        title: "Cuando el flete falla, falla su operación",
        paragraphs: [
          "Una entrega que no llega a tiempo detiene una línea de producción, deja un anaquel vacío o le cuesta una penalización con su cliente. Y lo que más desgasta es no saber dónde está la carga ni a quién preguntarle.",
          "Por eso trabajamos con flota propia, rastreo en cada unidad y reportes de cada viaje: para que usted sepa en todo momento qué está pasando con su mercancía.",
        ],
      },
      {
        title: "Cómo es nuestro servicio de carga seca",
        list: [
          "Cajas secas de 53 pies para mercancía general, paletizada o a granel.",
          "Tracto-camiones propios, revisados en nuestro taller y en renovación continua.",
          "Doble GPS en cada tracto y rastreo independiente en cada remolque, integrable a la plataforma de su empresa.",
          "Reporte documentado de cada embarque.",
          "Seguro de carga a solicitud y almacenaje como servicio extra.",
        ],
      },
      {
        title: "Rutas",
        paragraphs: [
          `Salimos de Delicias, Chihuahua, y cubrimos ${ESTADOS}, además de los puertos de Altamira y Manzanillo. Si necesita un origen o destino distinto, lo cotizamos.`,
        ],
      },
    ],
    faqs: [
      {
        q: "¿Cuánto cuesta un flete de carga seca?",
        a: "Depende de la ruta, el peso, el tipo de mercancía y la frecuencia de envíos. Mándenos esos datos y le enviamos una cotización a la medida.",
      },
      {
        q: "¿Qué tipo de mercancía transportan?",
        a: "Mercancía general, paletizada o a granel, en cajas secas de 53 pies.",
      },
      {
        q: "¿Tienen rastreo GPS?",
        a: "Sí. Doble sistema de GPS en cada tracto-camión y rastreo independiente en cada remolque, 24/7.",
      },
      {
        q: "¿Qué cobertura tienen?",
        a: `Centro y norte de México (${ESTADOS}) y los puertos de Altamira y Manzanillo, con salida desde Delicias, Chihuahua.`,
      },
      {
        q: "¿Puedo asegurar mi carga?",
        a: "Sí, el seguro de carga está disponible a solicitud.",
      },
    ],
    related: ["refrigerada", "delicias", "chihuahua"],
  },

  refrigerada: {
    path: "/carga-refrigerada/",
    title: "Transporte de carga refrigerada en Chihuahua | Transportes CGMG",
    description:
      "Transporte de carga refrigerada desde Delicias, Chihuahua: unidad Thermo de 53 pies con control de temperatura y rastreo GPS 24/7. Cotice su flete.",
    breadcrumb: "Carga refrigerada",
    service: "Transporte de carga refrigerada",
    hero: {
      eyebrow: "Servicios · Carga refrigerada",
      h1: "Transporte de carga refrigerada",
      lead: "Unidad Thermo de 53 pies con control de temperatura para producto perecedero, con rastreo GPS durante todo el viaje.",
    },
    sections: [
      {
        title: "El producto perecedero no espera",
        paragraphs: [
          "Con carga refrigerada, un retraso o una falla en la cadena de frío se traduce directamente en producto perdido. Necesita una unidad en buen estado, un operador con experiencia y visibilidad del viaje de principio a fin.",
        ],
      },
      {
        title: "Nuestro servicio refrigerado",
        list: [
          "Unidad Thermo de 53 pies con control de temperatura.",
          "Mantenimiento en nuestro propio taller, en Delicias.",
          "Doble GPS en el tracto-camión y rastreo independiente en el remolque, las 24 horas.",
          "Reporte documentado del viaje.",
          "Seguro de carga disponible a solicitud.",
        ],
        note: "Por ser equipo especializado, le recomendamos solicitar su cotización con anticipación para confirmar disponibilidad en la fecha que necesita.",
      },
      {
        title: "Rutas",
        paragraphs: [
          `Operamos desde Delicias, Chihuahua, en rutas hacia ${ESTADOS}, y hacia los puertos de Altamira y Manzanillo.`,
        ],
      },
    ],
    faqs: [
      {
        q: "¿Cuánto cuesta un flete refrigerado?",
        a: "Depende de la ruta, el producto, el rango de temperatura y la fecha. Envíenos esos datos y le preparamos una cotización.",
      },
      {
        q: "¿Qué temperatura pueden mantener?",
        a: "Nuestra unidad Thermo tiene control de temperatura. Indíquenos el rango que requiere su producto y le confirmamos si podemos cumplirlo antes de cotizar.",
      },
      {
        q: "¿Tienen rastreo GPS?",
        a: "Sí, con monitoreo 24/7: doble GPS en el tracto-camión y rastreo independiente en el remolque.",
      },
      {
        q: "¿Qué cobertura tienen para carga refrigerada?",
        a: `Rutas desde Delicias hacia ${ESTADOS}, y los puertos de Altamira y Manzanillo.`,
      },
    ],
    related: ["seca", "delicias", "chihuahua"],
  },
};

export const SEO_PAGE_IDS = ["delicias", "chihuahua", "seca", "refrigerada"];
